import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./AnalysisProgressModal.css";

const CodeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polyline points="16 18 22 12 16 6"></polyline>
    <polyline points="8 6 2 12 8 18"></polyline>
  </svg>
);

const BrushIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08"></path>
    <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z"></path>
  </svg>
);

const BarChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="12" y1="20" x2="12" y2="10"></line>
    <line x1="18" y1="20" x2="18" y2="4"></line>
    <line x1="6" y1="20" x2="6" y2="16"></line>
  </svg>
);

const TableIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="3" y1="9" x2="21" y2="9"></line>
    <line x1="3" y1="15" x2="21" y2="15"></line>
    <line x1="9" y1="3" x2="9" y2="21"></line>
    <line x1="15" y1="3" x2="15" y2="21"></line>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const AnalysisProgressModal = ({
  open,
  onClose,
  onDone,
  title,
  steps = [],
  currentStep = 0,
  autoplay = false,
  stepDurationMs = 1500,
}) => {
  const { t } = useTranslation();
  const [localCurrentStep, setLocalCurrentStep] = useState(0);

  const activeStep = currentStep >= 0 ? currentStep : localCurrentStep;

  // ✅ Mapping des étapes par défaut avec traduction
  const defaultSteps = [
    {
      icon: CodeIcon,
      title: t('analysisProgres.steps.classification.title'),
      description: t('analysisProgres.steps.classification.description'),
    },
    {
      icon: BrushIcon,
      title: t('analysisProgres.steps.ocr.title'),
      description: t('analysisProgres.steps.ocr.description'),
    },
    {
      icon: BarChartIcon,
      title: t('analysisProgres.steps.extraction.title'),
      description: t('analysisProgres.steps.extraction.description'),
    },
    {
      icon: TableIcon,
      title: t('analysisProgres.steps.normalize.title'),
      description: t('analysisProgres.steps.normalize.description'),
    },
    {
      icon: CodeIcon,
      title: t('analysisProgres.steps.anonymization.title'),
      description: t('analysisProgres.steps.anonymization.description'),
    },
  ];

  // Si steps est un tableau de strings, mapper avec les icônes par défaut
  const mappedSteps =
    steps.length > 0 && typeof steps[0] === "string"
      ? steps.map((stepLabel, idx) => ({
          icon: defaultSteps[idx % defaultSteps.length].icon,
          title: stepLabel,
          description: defaultSteps[idx % defaultSteps.length].description,
        }))
      : steps.length > 0
      ? steps
      : defaultSteps;

  useEffect(() => {
    if (!open || !autoplay) {
      setLocalCurrentStep(0);
      return;
    }

    const interval = setInterval(() => {
      setLocalCurrentStep((prev) => {
        const next = prev + 1;
        if (next >= mappedSteps.length) {
          clearInterval(interval);
          if (onDone) {
            setTimeout(() => onDone(), 300);
          }
          return prev;
        }
        return next;
      });
    }, stepDurationMs);

    return () => clearInterval(interval);
  }, [open, autoplay, stepDurationMs, onDone, mappedSteps.length]);

  const handleCloseProgress = () => {
    setLocalCurrentStep(0);
    onClose();
  };

  if (!open) return null;

  const currentStepData = mappedSteps[activeStep] || mappedSteps[0];
  const CurrentIconComponent = currentStepData.icon;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <button
          onClick={handleCloseProgress}
          className="close-button"
          aria-label={t('analysisProgres.close')}
        >
          <CloseIcon />
        </button>

        <div className="modal-content">
          <h2 className="modal-title">{title || t('analysisProgres.defaultTitle')}</h2>

          <div className="icon-wrapper">
            <div className="icon-circle">
              <div className="icon-background"></div>
              <div className="icon-container" key={activeStep}>
                <CurrentIconComponent />
              </div>
            </div>
          </div>

          <h3 className="step-title" key={`title-${activeStep}`}>
            {currentStepData.title}
          </h3>

          <p className="step-description" key={`desc-${activeStep}`}>
            {currentStepData.description}
          </p>

          <div className="progress-indicators">
            {mappedSteps.map((_, i) => (
              <div
                key={i}
                className={`progress-dot ${
                  i === activeStep
                    ? "active"
                    : i < activeStep
                    ? "completed"
                    : "pending"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisProgressModal;